/* =========================================================================
   DDS FAVORITES ENGINE
   - Fara cont: localStorage, expirar 30 zile
   - Cu cont Supabase: sincronizare in dds_favorites
   - La login: migrare automata localStorage → Supabase
========================================================================= */

(function() {
  'use strict';

  const DDS_FAV_KEY = 'dds_favorites_v1';
  const EXPIRY_DAYS = 30;
  const EXPIRY_MS   = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  /* ===== LOCAL STORAGE HELPERS ===== */

  function lsGet() {
    try {
      const raw = localStorage.getItem(DDS_FAV_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      const now = Date.now();
      return arr.filter(f => {
        if (!f.added_at) return true;
        const ts = typeof f.added_at === 'number' ? f.added_at : Date.parse(f.added_at);
        return !isNaN(ts) && (now - ts) < EXPIRY_MS;
      });
    } catch(e) { return []; }
  }

  function lsSet(arr) {
    try { localStorage.setItem(DDS_FAV_KEY, JSON.stringify(arr)); } catch(e) {}
  }

  function lsAdd(product) {
    const arr = lsGet();
    const exists = arr.find(f => f.product_handle === product.product_handle);
    if (!exists) {
      arr.push({ ...product, added_at: Date.now() });
      lsSet(arr);
    }
  }

  function lsRemove(handle) {
    const arr = lsGet().filter(f => f.product_handle !== handle);
    lsSet(arr);
  }

  function lsHas(handle) {
    return lsGet().some(f => f.product_handle === handle);
  }

  /* ===== SUPABASE HELPERS ===== */

  async function sbAdd(sb, product) {
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return;
    await sb.from('dds_favorites').upsert({
      user_id: session.user.id,
      product_handle: product.product_handle,
      product_title: product.product_title,
      product_image_url: product.product_image_url,
      product_price: product.product_price,
      product_url: product.product_url
    }, { onConflict: 'user_id,product_handle' });
  }

  async function sbRemove(sb, handle) {
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return;
    await sb.from('dds_favorites').delete()
      .eq('user_id', session.user.id)
      .eq('product_handle', handle);
  }

  async function sbList(sb) {
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return [];
    const { data } = await sb.from('dds_favorites')
      .select('product_handle, product_title, product_image_url, product_price, product_url, added_at')
      .eq('user_id', session.user.id)
      .order('added_at', { ascending: false });
    return data || [];
  }

  async function sbHas(sb, handle) {
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return false;
    const { data } = await sb.from('dds_favorites')
      .select('product_handle')
      .eq('user_id', session.user.id)
      .eq('product_handle', handle)
      .maybeSingle();
    return !!data;
  }

  /* ===== MIGRARE localStorage → Supabase la login ===== */

  async function migrateLocalToSupabase(sb) {
    const local = lsGet();
    if (!local.length) return;
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return;
    const byHandle = new Map();
    for (const item of local) {
      if (item && item.product_handle) {
        byHandle.set(item.product_handle, {
          user_id: session.user.id,
          product_handle: item.product_handle,
          product_title: item.product_title,
          product_image_url: item.product_image_url,
          product_price: item.product_price,
          product_url: item.product_url
        });
      }
    }
    const rows = Array.from(byHandle.values());
    if (!rows.length) return;
    const { error } = await sb.from('dds_favorites').upsert(rows, { onConflict: 'user_id,product_handle' });
    if (!error) {
      lsSet([]); // sterge dupa migrare
      window.dispatchEvent(new CustomEvent('dds:favorites:migrated'));
    }
  }

  /* ===== THEMARKETER WISHLIST TRACKING (dataLayer) ===== */

  function emitMktrWishlistEvent(action, productId, sku) {
    if (!productId) return;
    const pid = Number(productId) || productId;
    const skuVal = (sku && String(sku).trim()) ? String(sku).trim() : pid;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: action === 'add' ? '__sm__add_to_wishlist' : '__sm__remove_from_wishlist',
      product_id: pid,
      quantity: 1,
      variation: { id: pid, sku: skuVal }
    });
  }

  function pushTheMarketerWishlist(action, itemOrHandle) {
    try {
      const obj = (typeof itemOrHandle === 'object' && itemOrHandle) ? itemOrHandle : null;
      const handle = obj ? obj.product_handle : String(itemOrHandle || '');
      let productId = obj && obj.product_id ? obj.product_id : '';
      let sku = obj && obj.sku ? obj.sku : '';
      if (!productId && handle) {
        const domBtn = document.querySelector(`[data-handle="${handle}"][data-product-id]`);
        if (domBtn && domBtn.dataset.productId) {
          productId = domBtn.dataset.productId;
          sku = domBtn.dataset.sku || '';
        }
      }
      if (productId) { emitMktrWishlistEvent(action, productId, sku); return; }
      if (handle) {
        fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && data.id) {
              const firstVar = (data.variants && data.variants[0]) || {};
              emitMktrWishlistEvent(action, data.id, firstVar.sku || '');
            }
          })
          .catch(() => {});
      }
    } catch (e) {}
  }

  /* ===== API PUBLIC ===== */

  window.DDS_FAV = {
    async isLoggedIn() {
      if (!window.ddsSupabase) return false;
      const { data: { session } } = await window.ddsSupabase.auth.getSession();
      return !!session;
    },

    async add(product) {
      // Salvează ÎNTOTDEAUNA local pe loc ca buffer de siguranță suprem!
      lsAdd(product);
      pushTheMarketerWishlist('add', product);
      
      try {
        if (await this.isLoggedIn()) {
          await sbAdd(window.ddsSupabase, product);
        }
      } catch (e) {
        console.warn("Eșec la sincronizare Supabase, dar produsul a fost salvat local.", e);
      }
      
      window.dispatchEvent(new CustomEvent('dds:favorites:change', { detail: product.product_handle }));
    },

    async remove(handle) {
      const existingLocal = lsGet().find(f => f.product_handle === handle);
      lsRemove(handle); // Forțează eliminarea locală imediat
      pushTheMarketerWishlist('remove', existingLocal || handle);
      try {
        if (await this.isLoggedIn()) {
          await sbRemove(window.ddsSupabase, handle);
        }
      } catch (e) {}
      window.dispatchEvent(new CustomEvent('dds:favorites:change', { detail: handle }));
    },

    async toggle(product) {
      const active = await this.has(product.product_handle);
      if (active) {
        await this.remove(product.product_handle);
        return false;
      } else {
        await this.add(product);
        return true;
      }
    },

    async has(handle) {
      if (await this.isLoggedIn()) {
        return sbHas(window.ddsSupabase, handle);
      }
      return lsHas(handle);
    },

    async list() {
      const localItems = lsGet();
      if (await this.isLoggedIn()) {
        try {
          const sbItems = await sbList(window.ddsSupabase);
          const localMap = new Map(localItems.map(i => [i.product_handle, i]));
          const merged = sbItems.map(item => {
            const loc = localMap.get(item.product_handle);
            return loc ? { ...loc, ...item } : item;
          });
          lsSet(merged);
          return merged;
        } catch (e) {
          return localItems;
        }
      }
      return localItems;
    },

    async count() {
      const items = await this.list();
      return items.length;
    },

    isGuest() {
      return !window.ddsSupabase ||
        window.ddsSupabase.auth.getSession().then(r => !r.data.session);
    }
  };

  /* ===== INITIALIZARE BUTOANE ===== */

  async function initButtons() {
    const btns = document.querySelectorAll('.dds-fav-btn[data-handle]');
    if (!btns.length) return;
    try {
      const favList = await window.DDS_FAV.list();
      const favSet = new Set(favList.map(f => f.product_handle));
      btns.forEach(btn => {
        const handle = btn.dataset.handle;
        const isActive = favSet.has(handle);
        btn.classList.toggle('is-active', isActive);
      });
    } catch(e) {
      for (const btn of btns) {
        const handle = btn.dataset.handle;
        const isActive = await window.DDS_FAV.has(handle);
        btn.classList.toggle('is-active', isActive);
      }
    }
  }

  function getProductFromBtn(btn) {
    return {
      product_id:        btn.dataset.productId || '',
      variant_id:        btn.dataset.variantId || '',
      sku:               btn.dataset.sku       || '',
      product_handle:    btn.dataset.handle    || '',
      product_title:     btn.dataset.title     || '',
      product_image_url: btn.dataset.image     || '',
      product_price:     parseFloat(btn.dataset.price || '0'),
      product_url:       btn.dataset.url       || window.location.pathname
    };
  }

  /* ===== EVENT DELEGATION — click pe orice .dds-fav-btn ===== */

  document.addEventListener('click', async function(e) {
    const btn = e.target.closest('.dds-fav-btn[data-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    btn.classList.add('dds-fav-pop');
    setTimeout(() => btn.classList.remove('dds-fav-pop'), 350);

    const product = getProductFromBtn(btn);
    const nowActive = await window.DDS_FAV.toggle(product);

    // Update toate butoanele cu acelasi handle
    document.querySelectorAll(`.dds-fav-btn[data-handle="${product.product_handle}"]`).forEach(b => {
      b.classList.toggle('is-active', nowActive);
      const label = b.querySelector('.dds-fav-label');
      if (label) label.textContent = nowActive ? 'Eliminat din Favorite' : 'Adaugă la Favorite';
    });

    // Feedback toast
    showToast(nowActive ? '❤️ Adăugat la favorite!' : '💔 Eliminat din favorite');
  });

  /* ===== TOAST NOTIFICATION ===== */

  function showToast(msg) {
    let toast = document.getElementById('dds-fav-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dds-fav-toast';
      toast.style.cssText = `
        position: fixed; bottom: 100px; left: 50%; transform: translateX(-50%);
        background: #1e293b; color: #fff; padding: 12px 24px; border-radius: 50px;
        font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 600;
        z-index: 200001; opacity: 0; transition: opacity 0.3s, bottom 0.3s;
        pointer-events: none; white-space: nowrap; box-shadow: 0 4px 20px rgba(0,0,0,0.25);
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.opacity = '1';
    toast.style.bottom = '90px';
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.bottom = '80px';
    }, 2200);
  }

  /* ===== ACTUALIZEAZA COUNTER FAVORITE IN HEADER ===== */

  async function updateFavCount() {
    const count = await window.DDS_FAV.count();
    document.querySelectorAll('.dds-fav-count').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  window.addEventListener('dds:favorites:change', updateFavCount);
  window.addEventListener('dds:favorites:migrated', () => {
    initButtons();
    updateFavCount();
  });

  /* ===== MIGRARE LA LOGIN ===== */

  function waitForSupabase(retries = 20) {
    if (window.ddsSupabase) {
      // Logat sau supabase disponibil
      window.ddsSupabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          try { sessionStorage.removeItem('dds_mktr_email'); } catch (e) {}
        } else if (session.user && session.user.email) {
          try {
            if (sessionStorage.getItem('dds_mktr_email') !== session.user.email) {
              sessionStorage.setItem('dds_mktr_email', session.user.email);
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: '__sm__set_email', email_address: session.user.email });
            }
          } catch (e) {}
        }
        if (event === 'SIGNED_IN') {
          await migrateLocalToSupabase(window.ddsSupabase);
          initButtons();
          updateFavCount();
        }
      });
      initButtons();
      updateFavCount();
    } else if (retries > 0) {
      setTimeout(() => waitForSupabase(retries - 1), 200);
    } else {
      // Supabase indisponibil (guest fara SDK) — citim din localStorage
      initButtons();
      updateFavCount();
    }
  }

  /* ===== UI RENDERER GLOBAL ===== */
  window.DDS_FAV_UI = {
    async loadFavorites(gridSelector, loadingSelector, guestBannerSelector) {
      if (!window.DDS_FAV) return;

      const grid = gridSelector ? document.querySelector(gridSelector) : null;
      const loading = loadingSelector ? document.querySelector(loadingSelector) : null;
      const guestBanner = guestBannerSelector ? document.querySelector(guestBannerSelector) : null;

      if (!grid) return;

      function showGrid() {
        if (loading) loading.style.display = 'none';
        grid.style.display = 'grid';
      }

      function formatPrice(val) {
        if (!val && val !== 0) return '';
        const n = parseFloat(val);
        if (isNaN(n)) return '';
        return n.toFixed(2).replace('.', ',') + ' Lei';
      }

      function renderCard(item) {
        const rawPriceNum = parseFloat(item.product_price);
        const bfFavPct = (window.ddsBfExtraPct && window.ddsBfExtraPct > 0 && window.ddsBfShowOnPdp) ? window.ddsBfExtraPct : 0;
        const effPriceNum = (!isNaN(rawPriceNum) && bfFavPct > 0) ? (Math.round(rawPriceNum * (100 - bfFavPct)) / 100) : rawPriceNum;
        const price = formatPrice(effPriceNum);
        const oldPrice = (!isNaN(rawPriceNum) && bfFavPct > 0 && rawPriceNum > effPriceNum) ? formatPrice(rawPriceNum) : '';
        const url   = item.product_url || ('/products/' + item.product_handle);
        const img   = item.product_image_url || '';
        const title = item.product_title || item.product_handle;
        const handle = item.product_handle;

        return [
          `<div class="dds-fav-card" data-handle="${handle}">`,
            `<div onclick="window.location.href='${url}'" class="dds-fav-card__img-wrap" style="cursor:pointer; text-decoration:none;">`,
              img ? `<img class="dds-fav-card__img" src="${img}" alt="${title.replace(/"/g,'')}" loading="lazy">` : `<div style="width:74px;height:74px;background:#e2e8f0;border-radius:8px;"></div>`,
            `</div>`,
            `<div class="dds-fav-card__body">`,
              `<div onclick="window.location.href='${url}'" style="cursor:pointer; text-decoration:none;">`,
                `<p class="dds-fav-card__title">${title}</p>`,
              `</div>`,
              price ? `<p class="dds-fav-card__price">${oldPrice ? `<s style="opacity:0.75;margin-right:6px;font-weight:700;">${oldPrice}</s>` : ''}${price}</p>` : '',
              `<div class="dds-fav-card__actions">`,
                `<button onclick="window.location.href='${url}'" class="dds-fav-card__buy">Vezi produsul</button>`,
                `<button class="dds-fav-card__remove" data-handle="${handle}" data-product-id="${item.product_id || ''}" data-sku="${item.sku || ''}" aria-label="Șterge din favorite" title="Șterge din favorite">`,
                  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">`,
                    `<polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14H6L5 6"></path><path d="M10 11v6M14 11v6"></path><path d="M9 6V4h6v2"></path>`,
                  `</svg>`,
                `</button>`,
              `</div>`,
            `</div>`,
          `</div>`
        ].join('');
      }

      async function renderEmpty() {
        if (loading) loading.style.display = 'none';
        grid.style.display = 'block'; // force block layout pt asezare centrala
        grid.innerHTML = `
          <div class="dds-favpage__empty">
            <div class="dds-favpage__empty-icon">🤍</div>
            <h3>Nu ai niciun produs favorit</h3>
            <p>Apasă pe ❤️ de pe orice produs pentru a-l salva aici.</p>
            <a href="/collections/all" class="dds-favpage__start-btn">Vezi toate produsele</a>
            <a href="/pages/cont" class="dds-favpage__account-btn dds-auth-trigger" data-auth-mode="login"><svg style="vertical-align:middle; width:20px; height:20px; margin-right:6px;" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path></svg>Mergi la cont</a>
          </div>
        `;
      }

      try {
        const isLoggedIn = await window.DDS_FAV.isLoggedIn();
        if (guestBanner) guestBanner.style.display = isLoggedIn ? 'none' : 'flex';

        const items = await window.DDS_FAV.list();
        if (!items || !items.length) {
          renderEmpty();
          return;
        }

        showGrid();
        grid.innerHTML = items.map(renderCard).join('');

        // Unique listener attachment
        if (!grid._hasFavListener) {
          grid.addEventListener('click', async function(e) {
            const btn = e.target.closest('.dds-fav-card__remove');
            if (!btn) return;
            const handle = btn.dataset.handle;
            await window.DDS_FAV.remove(handle);
            const card = grid.querySelector(`.dds-fav-card[data-handle="${handle}"]`);
            if (card) {
              card.style.transition = 'opacity 0.3s, transform 0.3s';
              card.style.opacity  = '0';
              card.style.transform = 'scale(0.92)';
              setTimeout(() => {
                card.remove();
                if (!grid.querySelectorAll('.dds-fav-card').length) renderEmpty();
              }, 300);
            }
          });
          grid._hasFavListener = true;
        }

      } catch(err) {
        if (loading) loading.style.display = 'none';
        console.warn('DDS Favorites UI Error:', err);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSupabase());
  } else {
    waitForSupabase();
  }

})();
